import { useVirtualizer } from '@tanstack/react-virtual';
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import {
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollThumb,
  VerticalScrollbar,
} from '../../../atoms';
import { cn } from '../../../utils';
import { VirtualMultiselectItemContext } from '../contexts/item.context';
import { useVirtualMultiselectRoot } from '../contexts/root.context';
import { VirtualMultiselectWindowContext } from '../contexts/window.context';

interface VirtualMultiselectWindowProps<Item>
  extends HTMLAttributes<HTMLDivElement> {
  estimateHeight?: (idx: number) => number;
  overscan?: number;
  items: Item[];
  renderItem: (item: Item) => ReactNode;
  getItemId: (item: Item) => string;
  getItemDisabled?: (item: Item) => boolean;
  /**
   * Callback function to be called when the bottom of the viewport is reached.
   */
  onScrollEnd?: () => void;
}

/**
 * A virtualised combobox window that renders a list of options. We always assume
 * dynamic heights for every item.
 */
const _VirtualMultiselectWindow = forwardRef<
  HTMLDivElement,
  VirtualMultiselectWindowProps<unknown>
>(
  (
    {
      estimateHeight = () => 40,
      overscan = 0,
      items,
      getItemId,
      getItemDisabled,
      renderItem,
      onScrollEnd,
      className,
      ...props
    },
    ref,
  ) => {
    const { setAvailableItemsSet, value } = useVirtualMultiselectRoot();
    const windowRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => windowRef.current as HTMLDivElement);

    const [observerRef] = useIntersectionObserver({
      root: windowRef.current,
      threshold: 0,
      onChange: useCallback(
        (isIntersecting: boolean) => {
          if (isIntersecting) {
            onScrollEnd?.();
          }
        },
        [onScrollEnd],
      ),
    });

    const rowVirtualizer = useVirtualizer({
      count: items.length,
      getScrollElement: () => windowRef.current,
      estimateSize: estimateHeight,
      overscan,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalVirtualSize = rowVirtualizer.getTotalSize();

    const virtualOptions = useMemo(
      () =>
        virtualItems.map((item) => ({
          item: items[item.index],
          virtualItem: item,
        })),
      [virtualItems, items],
    );

    useEffect(() => {
      setAvailableItemsSet(
        new Set(
          items.filter((item) => !getItemDisabled?.(item)).map(getItemId),
        ),
      );
    }, [items, getItemId, getItemDisabled, setAvailableItemsSet]);

    const { measureElement } = rowVirtualizer;

    return (
      <VirtualMultiselectWindowContext.Provider value={{ measureElement }}>
        <ScrollAreaRoot type="scroll">
          <ScrollAreaViewport asChild>
            <div
              ref={windowRef}
              className={cn(
                'relative border rounded-sm bg-white shadow-sm',
                className,
              )}
              {...props}
            >
              <div
                className={cn('relative w-full')}
                style={{ height: `${totalVirtualSize}px` }}
              >
                {virtualOptions.map(({ item, virtualItem }) => {
                  const id = getItemId(item);
                  return (
                    <VirtualMultiselectItemContext.Provider
                      key={id}
                      value={{
                        id,
                        virtualItem,
                        disabled: getItemDisabled?.(item) ?? false,
                        selected: value.includes(id),
                      }}
                    >
                      {renderItem(item)}
                    </VirtualMultiselectItemContext.Provider>
                  );
                })}
                <div
                  ref={observerRef}
                  className="absolute bottom-0 left-0 h-10 w-full bg-primary opacity-0 pointer-events-none"
                />
              </div>
            </div>
          </ScrollAreaViewport>
          <VerticalScrollbar className="mr-0.5 my-0.5">
            <ScrollThumb />
          </VerticalScrollbar>
        </ScrollAreaRoot>
      </VirtualMultiselectWindowContext.Provider>
    );
  },
);

_VirtualMultiselectWindow.displayName = 'VirtualMultiselectWindow';

export const VirtualMultiselectWindow = _VirtualMultiselectWindow as <Item>(
  props: VirtualMultiselectWindowProps<Item> & RefAttributes<HTMLDivElement>,
) => ReactNode;
